import { InfoOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Collapse,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import InfoButton, { TInfoButton } from '~/components/infoBlock/infoButton'
import rem from '~/utils/ui'

type TInfoBlockProps = {
  icon: React.ReactNode
  title: string
  value: React.ReactNode
  actionButtons: TInfoButton[]
  isExpanded: boolean
  infoText: React.ReactNode
  onLabelClick?: () => void
}

function InfoBlock({
  icon,
  title,
  value,
  actionButtons,
  isExpanded,
  infoText,
  onLabelClick = () => {},
}: TInfoBlockProps) {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xxxs: 0, lg: 0.5 },
        '& .MuiSvgIcon-root': {
          color: theme.palette.tertiary1,
          width: rem(19),
          height: rem(20),
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: { xxxs: 0.5, lg: 0 },
          flexDirection: {
            xxxs: isExpanded ? 'column' : 'row',
            lg: 'column',
          },
        }}
      >
        <Typography
          fontSize={rem(14)}
          fontWeight="medium"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {icon}
          {title}
          <Box
            component="span"
            sx={{
              display: { xxxs: isExpanded ? 'none' : 'inline', lg: 'none' },
              ml: -1,
            }}
          >
            :
          </Box>
        </Typography>
        <Button
          disableRipple
          onClick={onLabelClick}
          sx={{
            fontSize: { xxxs: rem(14), lg: rem(12) },
            fontWeight: 400,
            letterSpacing: 0,
            color: theme.palette.text.primary,
            lineHeight: 'unset',
            whiteSpace: 'wrap',
            textAlign: 'left',
            '&:hover': {
              backgroundColor: 'initial',
              textDecoration: 'underline',
            },
            '@media (min-width: 0px)': {
              height: 'initial',
              padding: 0,
            },
          }}
          variant="text"
        >
          {value}
        </Button>
      </Box>

      <Collapse in={isDesktop ? true : isExpanded}>
        <Stack
          direction="row"
          spacing={0}
          alignItems="center"
          flexWrap={{ xxxs: 'wrap', lg: 'nowrap' }}
          gap={1}
          sx={{ pt: { xxxs: 1, lg: 0 } }}
        >
          {actionButtons.map((actionButtonProps) => (
            <InfoButton
              key={`${actionButtonProps.label}-${actionButtonProps.variant}`}
              {...actionButtonProps}
            />
          ))}
          <Tooltip
            enterTouchDelay={0}
            title={
              typeof infoText === 'string' ? (
                <Box sx={{ whiteSpace: 'pre-line' }}>{infoText}</Box>
              ) : (
                infoText
              )
            }
          >
            <InfoOutlined
              sx={{
                fontSize: rem(14),
                color: `${theme.palette.text.secondary} !important`,
              }}
            />
          </Tooltip>
        </Stack>
      </Collapse>
    </Box>
  )
}

export default InfoBlock
